import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TrendingUp, Package, BarChart4, ShoppingBag, CreditCard, FileText, AlertTriangle, Factory, XCircle, Wallet } from 'lucide-react';
import { SalesReport, TopProducts, PaymentMethodReport, DetailedSalesReport, InventoryConsumptionReport, StockReport, DifferencesReport, ProductionEfficiencyReport, InventoryErrorsReport, CashRegisterReport } from './components';
import { useAuth } from '@/context/AuthContext';

type ReportCategory = 'sales' | 'inventory' | 'operations';
type ReportTab = 'sales' | 'products' | 'payment' | 'detailed' | 'consumption' | 'stock' | 'differences' | 'production' | 'errors' | 'cashRegister';

export const ReportsPage: React.FC = () => {
  const { user } = useAuth();
  const [activeCategory, setActiveCategory] = useState<ReportCategory>('sales');
  const [activeTab, setActiveTab] = useState<ReportTab>('sales');

  // Verificar si el usuario es CAJERO (positionId = 3)
  const isCashier = user?.positionId === 3;

  // Configuración de reportes por categoría
  const reportCategories = {
    sales: {
      label: 'Ventas',
      icon: TrendingUp,
      color: 'bg-blue-500',
      reports: [
        { key: 'sales', label: 'Reporte General', icon: TrendingUp },
        { key: 'products', label: 'Productos Top', icon: ShoppingBag },
        { key: 'payment', label: 'Modalidades de Pago', icon: CreditCard },
        { key: 'detailed', label: 'Ventas Detalladas', icon: FileText }
      ]
    },
    inventory: {
      label: 'Inventario',
      icon: Package,
      color: 'bg-green-500',
      reports: [
        { key: 'consumption', label: 'Consumo de Insumos', icon: Package },
        { key: 'stock', label: 'Stock Actual', icon: BarChart4 },
        { key: 'differences', label: 'Diferencias', icon: AlertTriangle },
        { key: 'errors', label: 'Errores de Inventario', icon: XCircle }
      ]
    },
    operations: {
      label: 'Operaciones',
      icon: Factory,
      color: 'bg-purple-500',
      reports: [
        { key: 'cashRegister', label: 'Reporte de Caja', icon: Wallet },
        { key: 'production', label: 'Producción y Rendimiento', icon: Factory }
      ]
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'sales':
        return <SalesReport />;
      case 'products':
        return <TopProducts />;
      case 'payment':
        return <PaymentMethodReport />;
      case 'detailed':
        return <DetailedSalesReport />;
      case 'consumption':
        return <InventoryConsumptionReport />;
      case 'stock':
        return <StockReport />;
      case 'differences':
        return <DifferencesReport />;
      case 'cashRegister':
        return <CashRegisterReport />;
      case 'production':
        return <ProductionEfficiencyReport />;
      case 'errors':
        return <InventoryErrorsReport />;
      default:
        return <SalesReport />;
    }
  };

  const handleCategoryChange = (category: ReportCategory) => {
    setActiveCategory(category);
    // Cambiar al primer reporte de la nueva categoría
    const firstReport = reportCategories[category].reports[0];
    setActiveTab(firstReport.key as ReportTab);
  };

  const getCurrentCategoryReports = () => {
    return reportCategories[activeCategory].reports;
  };

  const getCurrentReportLabel = () => {
    const currentCategory = reportCategories[activeCategory];
    const currentReport = currentCategory.reports.find(report => report.key === activeTab);
    return currentReport?.label || 'Reporte';
  };

  // Vista simplificada para CAJERO
  if (isCashier) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Reporte de Ventas</h1>
            <p className="text-gray-600 mt-1">Ventas del día - {new Date().toLocaleDateString('es-PE')}</p>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg">
            <FileText className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">Solo lectura - Cajero</span>
          </div>
        </div>

        {/* Reporte directo sin filtros */}
        <DetailedSalesReport />
      </div>
    );
  }

  // Vista completa para otros roles
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Reportes y Análisis</h1>
      </div>

      {/* Navigation Bar */}
      <Card>
        <CardContent className="py-3">
          <div className="flex items-center gap-4">
            {/* Category Tabs */}
            <div className="flex items-center gap-1">
              {Object.entries(reportCategories).map(([key, category]) => {
                const IconComponent = category.icon;
                const isActive = activeCategory === key;
                return (
                  <button
                    key={key}
                    onClick={() => handleCategoryChange(key as ReportCategory)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-macartur-primary text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    {category.label}
                  </button>
                );
              })}
            </div>

            <div className="h-5 w-px bg-gray-300"></div>

            {/* Report Select */}
            <Select value={activeTab} onValueChange={(value) => setActiveTab(value as ReportTab)}>
              <SelectTrigger className="w-64">
                <SelectValue>{getCurrentReportLabel()}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {getCurrentCategoryReports().map((report) => {
                  const ReportIcon = report.icon;
                  return (
                    <SelectItem key={report.key} value={report.key}>
                      <div className="flex items-center gap-2">
                        <ReportIcon className="w-4 h-4" />
                        {report.label}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Report Content */}
      {renderContent()}
    </div>
  );
};